import {
  ArrowDown,
  ArrowDownRight,
  ArrowUp,
  ArrowUpRight,
  Calculator,
  DollarSign,
  Factory,
  Gauge,
  Info,
  Minus,
  TrendingUp,
  Wrench,
} from 'lucide-react';
import { KPIMetric } from '../../types/dashboard';
import {
  formatValue,
  formatActual,
  formatKpiValue,
  isCurrencyPerUnitKpi,
} from '../../utils/numberFormat';
import { cn } from '@/lib/utils';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../ui/tooltip';

interface KPICardProps {
  metric: KPIMetric;
  index: number;
  onClick?: () => void;
  isSelected?: boolean;
  onShowLineage?: (metric: KPIMetric) => void;
}

const iconByMetricName = {
  dollar: DollarSign,
  gauge: Gauge,
  factory: Factory,
  wrench: Wrench,
  calculator: Calculator,
  'arrow-up': ArrowUp,
  'arrow-down': ArrowDown,
  'trending-up': TrendingUp,
} as const;

function getMetricIcon(metric: KPIMetric) {
  return iconByMetricName[metric.icon as keyof typeof iconByMetricName] ?? TrendingUp;
}

type KpiTone = 'positive' | 'negative' | 'neutral';
type KpiComparator = NonNullable<KPIMetric['comparators']>[number];

// Repo-2 branding pass: mirror Repo2 card_layout slots while keeping them local to v2.
type LayoutSpec = {
  showSubline: boolean;
  maxChips: number;
  showSpark: boolean;
  showReferenceRows: boolean;
};

const layoutSpecs: Record<string, LayoutSpec> = {
  compact: { showSubline: false, maxChips: 1, showSpark: false, showReferenceRows: false },
  standard: { showSubline: false, maxChips: 3, showSpark: false, showReferenceRows: false },
  standard_with_spark: { showSubline: false, maxChips: 3, showSpark: true, showReferenceRows: false },
  detailed: { showSubline: true, maxChips: 5, showSpark: true, showReferenceRows: true },
  narrative: { showSubline: true, maxChips: 4, showSpark: true, showReferenceRows: true },
};

function layoutFor(metric: KPIMetric): LayoutSpec {
  return layoutSpecs[metric.cardLayout || 'standard_with_spark'] || layoutSpecs.standard_with_spark;
}

const toneClassNames: Record<KpiTone, {
  icon: string;
  value: string;
  rail: string;
  chip: string;
}> = {
  positive: {
    icon: 'border-[#3F7D4F]/25 bg-[#3F7D4F]/10 text-[#3F7D4F]',
    value: 'text-[#3F7D4F]',
    rail: 'bg-[#3F7D4F]',
    chip: 'border-[#3F7D4F]/35 bg-[#3F7D4F]/10 text-[#3F7D4F]',
  },
  negative: {
    icon: 'border-[#B23A48]/25 bg-[#B23A48]/10 text-[#B23A48]',
    value: 'text-[#B23A48]',
    rail: 'bg-[#B23A48]',
    chip: 'border-[#B23A48]/35 bg-[#B23A48]/10 text-[#B23A48]',
  },
  neutral: {
    icon: 'border-[#EDE6E1] bg-[#f5e9ee] text-[#6E3450]',
    value: 'text-[#1F1A20]',
    rail: 'bg-[#C9A24E]',
    chip: 'border-[#EDE6E1] bg-[#FAF7F1] text-[#7B6B72]',
  },
};

const comparatorToneClasses: Record<string, { text: string; border: string; bg: string; filledBg: string }> = {
  positive: { text: 'text-[#3F7D4F]', border: 'border-[#3F7D4F]/40', bg: 'bg-transparent', filledBg: 'bg-[#3F7D4F]/10' },
  negative: { text: 'text-[#B23A48]', border: 'border-[#B23A48]/40', bg: 'bg-transparent', filledBg: 'bg-[#B23A48]/10' },
  neutral: { text: 'text-[#7B6B72]', border: 'border-[#EDE6E1]', bg: 'bg-transparent', filledBg: 'bg-[#FAF7F1]' },
  primary: { text: 'text-[#4E7C89]', border: 'border-[#4E7C89]/40', bg: 'bg-transparent', filledBg: 'bg-[#4E7C89]/10' },
  accent: { text: 'text-[#C9A24E]', border: 'border-[#C9A24E]/50', bg: 'bg-transparent', filledBg: 'bg-[#C9A24E]/10' },
};

function parseSignedMetricValue(value: KPIMetric['value']): number | null {
  if (typeof value === 'number') return Number.isFinite(value) ? value : null;

  const raw = String(value ?? '').trim();
  if (!raw) return null;

  const normalized = raw
    .replace(/[−–—]/g, '-')
    .replace(/,/g, '');
  const parentheticalNegative = /^\s*\(.*\)\s*$/.test(normalized) && !normalized.includes('-');
  const match = normalized.match(/[+-]?\d+(?:\.\d+)?/);
  if (!match) return null;

  const parsed = Number(match[0]);
  if (!Number.isFinite(parsed)) return null;
  const signed = parentheticalNegative ? -Math.abs(parsed) : parsed;
  return Object.is(signed, -0) ? 0 : signed;
}

function isDirectionalMetric(metric: KPIMetric, sourceSummary: string | null): boolean {
  // Pull formula from the canonical lineage_v2 payload: the current
  // value's derivation formula carries the same "delta/variance/etc"
  // keyword signal the legacy calculationInfo.formula used to.
  const currentValue = metric.lineage_v2?.values?.find((v) => v.slot === 'current')
    ?? metric.lineage_v2?.values?.[0];
  const formula = currentValue?.derivation?.formula || '';
  const text = [metric.label, metric.category, metric.column, sourceSummary, formula]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();

  return /(?:\bdelta\b|[Δ∆]|\bvariance\b|\bchange\b|\bchg\b|\bdiff(?:erence)?\b|\bgap\b|\bvs\.?\b|\bversus\b|\byoy\b|\bmom\b|\bqoq\b|\bpp\b)/i.test(text);
}

function isInverseMetric(metric: KPIMetric, sourceSummary: string | null): boolean {
  const text = [metric.label, metric.category, metric.column, sourceSummary]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();

  const hasBadWhenHigherTerm = /\b(cost|expense|spend|waste|loss|ponc|defect|downtime|delay|scrap|return|refund|overrun)\b/i.test(text);
  const isSavingsMetric = /\b(saving|savings|reduction|reduced|decrease|decreased|lower)\b/i.test(text);
  return hasBadWhenHigherTerm && !isSavingsMetric;
}

function getDirectionalPresentation(metric: KPIMetric, _sourceSummary: string | null) {
  // The big number is always rendered NEUTRAL (black) — directional
  // signal (positive / negative) lives on the comparator chip via its
  // own backend-computed tone + arrow. ``isDirectionalMetric`` /
  // ``isInverseMetric`` are retained below for potential reuse but
  // are no longer driving the value colour, by design.
  return {
    Icon: getMetricIcon(metric),
    tone: 'neutral' as KpiTone,
  };
}

function getTrendTone(metric: KPIMetric): string {
  if (metric.trend === 'up') return toneClassNames.positive.chip;
  if (metric.trend === 'down') return toneClassNames.negative.chip;
  return toneClassNames.neutral.chip;
}

function shouldShowTrend(metric: KPIMetric): boolean {
  const value = metric.trendValue?.trim();
  if (!value) return false;
  const normalized = value.replace(/[+,%\s]/g, '');
  const numeric = Number(normalized);
  return metric.trend !== 'neutral' || (!Number.isNaN(numeric) && numeric !== 0);
}

function getSourceSummary(metric: KPIMetric): string | null {
  // One-line provenance hint surfaced on the KPI card. Sources, in
  // order: the metric's source column (sticky from the BE); the
  // current value's column_label from lineage_v2; the current value's
  // derivation formula. The legacy drillDown.steps / calculationInfo
  // fallbacks have been retired alongside the fields themselves.
  const currentValue = metric.lineage_v2?.values?.find((v) => v.slot === 'current')
    ?? metric.lineage_v2?.values?.[0];
  const summary = metric.column
    || currentValue?.column_label
    || currentValue?.derivation?.formula;
  if (!summary) return null;
  if (/^(direct calculation|direct value|calculation)$/i.test(summary.trim())) return null;
  return summary.replace(/\s+/g, ' ').trim();
}

function currencyRateSuffix(unit?: string): string {
  const match = unit?.trim().match(/^(?:usd|\$)\s*(\/\s*[a-z0-9]+)$/i);
  return match ? match[1].replace(/\s+/g, '').toUpperCase() : '';
}

function metricIsCurrencyPerUnit(metric: KPIMetric): boolean {
  return isCurrencyPerUnitKpi({
    unit: metric.unit,
    label: metric.label,
    sourceField: metric.source_field ?? metric.column,
  });
}

function formatNumericValue(value: number, unit?: string, currencyPerUnit = false) {
  const normalizedUnit = unit?.trim();
  const isUsd = !!normalizedUnit && (
    normalizedUnit === '$'
    || /^usd$/i.test(normalizedUnit)
    || currencyPerUnit
  );
  const isPct = !!normalizedUnit && (normalizedUnit === '%' || /^(pct|percent)$/i.test(normalizedUnit));

  // All display formatting flows through the canonical policy in
  // utils/numberFormat. The $ and % are split into prefix/suffix spans so
  // the card can style them separately from the headline number.
  if (isPct) {
    return { prefix: '', primary: formatValue(value, { unit: '%' }).replace(/%$/, ''), suffix: '%' };
  }
  if (isUsd) {
    const full = formatKpiValue(value, { unit: '$', currencyPerUnit });
    const sign = full.startsWith('-') ? '-' : '';
    const body = full.replace(/^-?\$/, '');
    return {
      prefix: `${sign}$`,
      primary: body,
      suffix: currencyPerUnit ? currencyRateSuffix(normalizedUnit) : '',
    };
  }
  if (!normalizedUnit) return { prefix: '', primary: formatValue(value), suffix: '' };
  return { prefix: '', primary: formatValue(value), suffix: normalizedUnit };
}

function formatMetricValueParts(metric: KPIMetric) {
  if (typeof metric.value === 'number' && Number.isFinite(metric.value)) {
    return formatNumericValue(metric.value, metric.unit, metricIsCurrencyPerUnit(metric));
  }

  const primary = String(metric.value ?? '--').trim() || '--';
  const normalizedUnit = metric.unit?.trim();
  if (!normalizedUnit) return { prefix: '', primary, suffix: '' };

  const primaryLower = primary.toLowerCase();
  const unitLower = normalizedUnit.toLowerCase();
  const alreadyContainsUnit = primaryLower.includes(unitLower) || (normalizedUnit === '$' && primary.includes('$'));

  if (alreadyContainsUnit) return { prefix: '', primary, suffix: '' };
  if (normalizedUnit === '$' || /^usd$/i.test(normalizedUnit)) return { prefix: '$', primary, suffix: '' };
  return { prefix: '', primary, suffix: normalizedUnit };
}

function formatComparatorValue(value?: number | null, unit?: string) {
  if (value == null || !Number.isFinite(Number(value))) return '--';
  const formatted = formatNumericValue(Number(value), unit);
  return `${formatted.prefix}${formatted.primary}${formatted.suffix}`;
}

function formatDeltaChipLabel(comparator: KpiComparator) {
  const deltaPct = comparator.delta_pct;
  if (deltaPct == null || !Number.isFinite(Number(deltaPct))) {
    return { label: '--', metric: comparator.delta_abs ?? 0 };
  }
  const sign = Number(deltaPct) > 0 ? '+' : '';
  return {
    label: `${sign}${Number(deltaPct).toFixed(1)}%`,
    metric: Number(deltaPct),
  };
}

function DeltaChip({ comparator, primary, unit }: { comparator: KpiComparator; primary: boolean; unit?: string }) {
  const { label: deltaLabel, metric } = formatDeltaChipLabel(comparator);
  const Icon = metric === 0 ? Minus : metric > 0 ? ArrowUpRight : ArrowDownRight;
  const toneKey = comparator.tone || 'neutral';
  const tone = comparatorToneClasses[toneKey] || comparatorToneClasses.neutral;
  const tooltip = [
    comparator.label ? `${comparator.label}: ${formatComparatorValue(comparator.value, unit)}` : '',
    comparator.delta_abs != null ? `Delta ${formatComparatorValue(comparator.delta_abs, unit)}` : '',
    deltaLabel !== '--' ? `(${deltaLabel})` : '',
  ].filter(Boolean).join(' ');

  return (
    <span
      title={tooltip || undefined}
      className={cn(
        "inline-flex max-w-full items-center gap-1 rounded-full border px-2.5 py-0.5 text-[11px] font-medium tabular-nums",
        tone.text,
        tone.border,
        primary ? tone.filledBg : tone.bg
      )}
    >
      <Icon className="h-3 w-3 shrink-0" />
      <span>{deltaLabel}</span>
      {comparator.label && <span className="truncate text-[10.5px] opacity-75">{comparator.label}</span>}
    </span>
  );
}

function ReferenceChip({ comparator }: { comparator: KpiComparator }) {
  const label = comparator.label || comparator.basis || 'Reference';
  return (
    <span
      title={label}
      className="inline-flex max-w-full items-center rounded-full border border-[#EDE6E1] bg-[#FAF7F1] px-2.5 py-0.5 text-[11px] font-medium text-[#7B6B72]"
    >
      <span className="truncate text-[10.5px]">{label}</span>
    </span>
  );
}

function ReferenceRow({ comparator, unit }: { comparator: KpiComparator; unit?: string }) {
  return (
    <div className="flex items-baseline justify-between gap-3 border-t border-[#EDE5D8]/80 py-1 first:border-t-0">
      <span className="truncate text-[11px] uppercase tracking-[0.06em] text-[#7B6B72]">
        {comparator.label || comparator.basis || 'Reference'}
      </span>
      <span className="font-display text-[14px] tabular-nums text-[#3F3338]">
        {formatComparatorValue(comparator.value, unit)}
      </span>
    </div>
  );
}

function MiniSparkline({ metric }: { metric: KPIMetric }) {
  const values = metric.spark?.values?.filter((value): value is number => typeof value === 'number' && Number.isFinite(value)) || [];
  if (values.length < 2) return null;
  const min = Math.min(...values);
  const max = Math.max(...values);
  const range = max - min || 1;
  const points = values.map((value, index) => {
    const x = (index / Math.max(1, values.length - 1)) * 100;
    const y = 28 - ((value - min) / range) * 24;
    return `${x},${y}`;
  }).join(' ');

  return (
    <div className="-mx-1 mt-1.5">
      <svg viewBox="0 0 100 32" role="img" aria-label={metric.spark?.label || 'KPI trend'} className="h-8 w-full overflow-visible">
        <polyline points={points} fill="none" stroke="#4E7C89" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
      {metric.spark?.label && (
        <div className="mt-0.5 text-[10px] italic text-[#7B6B72]">{metric.spark.label}</div>
      )}
    </div>
  );
}

// ``getMetadataRows`` removed — the only consumer (Source row at the
// foot of the card) was retired in favour of the lineage panel.

export function KPICard({ metric, index, onClick, isSelected, onShowLineage }: KPICardProps) {
  // Info icon is shown whenever the metric carries enough to drive
  // the LineagePanel (any values block on lineage_v2, OR any free-text
  // reasoning / comparator chips). The legacy drillDown / lineage
  // fields have been retired.
  const hasLineage = !!metric.lineage_v2 && (
    (metric.lineage_v2.values?.length ?? 0) > 0
    || (metric.lineage_v2.odata_refs?.length ?? 0) > 0
  );
  const hasInfoDetails = Boolean(
    hasLineage ||
    metric.reasoning ||
    metric.toolCallId ||
    metric.comparators?.length
  );
  const sourceSummary = getSourceSummary(metric);
  const presentation = getDirectionalPresentation(metric, sourceSummary);
  const tone = toneClassNames[presentation.tone];
  const showTrend = shouldShowTrend(metric);
  const valueParts = formatMetricValueParts(metric);
  const eyebrow = metric.label?.trim() || 'Key metric';
  const supportingLabel = metric.category?.trim();
  const layout = layoutFor(metric);
  const comingSoon = String(metric.availability || metric.status || '').toLowerCase() === 'coming_soon';
  const comparators = Array.isArray(metric.comparators) ? metric.comparators : [];
  const comparisonComparators = comparators.filter((comparator) => (comparator.mode || 'comparison') === 'comparison');
  const referenceComparators = comparators.filter((comparator) => (comparator.mode || 'comparison') === 'reference');
  const visibleChips = comparisonComparators.slice(0, layout.maxChips);
  const primaryChip = visibleChips.find((comparator) => comparator.is_primary) || visibleChips[0];
  const secondaryChips = visibleChips.filter((comparator) => comparator !== primaryChip);
  const referenceRows = referenceComparators.filter((comparator) => comparator.source_field);
  const referenceChips = referenceComparators
    .filter((comparator) => !comparator.source_field || !layout.showReferenceRows)
    .slice(0, Math.max(1, layout.maxChips - visibleChips.length));

  return (
    <article
      className={cn(
        // KPI tile inside the connected Key Numbers grid. Compact
        // padding + auto height — no large fixed min-height so cards
        // hug their content instead of leaving slack at the bottom.
        "group relative min-w-0 overflow-hidden bg-white p-3 transition-colors duration-200 md:p-3.5",
        comingSoon && "cursor-not-allowed bg-[#F5F5F5] text-[#8E858A]",
        isSelected && "z-10 ring-2 ring-inset ring-[#6E3450]/25"
      )}
      style={{ animationDelay: `${index * 50}ms` }}
    >
      <div className="flex h-full flex-col gap-1.5">
        <header className="flex items-start justify-between gap-2">
          <div className="min-w-0 flex-1 pr-2">
            <div className={cn(
              "break-words font-display text-[11px] font-semibold uppercase leading-tight tracking-[0.12em] [overflow-wrap:anywhere]",
              comingSoon ? "text-[#8E858A]" : "text-[#7B6B72]",
            )}>
              {eyebrow}
            </div>
            {metric.periodLabel && (
              <div className="mt-0.5 truncate text-[11px] text-[#A8989F]" title={metric.periodLabel}>
                {metric.periodLabel}
              </div>
            )}
            {!metric.periodLabel && supportingLabel && supportingLabel.toLowerCase() !== eyebrow.toLowerCase() && (
              <div className="mt-0.5 truncate text-[11px] text-[#A8989F]" title={supportingLabel}>
                {supportingLabel}
              </div>
            )}
          </div>

          <div className="ml-auto flex shrink-0 items-start justify-end gap-1.5">
            {/* Confidence pill removed — it was a self-reported LLM
                literal (High / Medium / Low) with no rubric behind it,
                misread as a quality score. Field also dropped from the
                backend schemas. */}
            {hasInfoDetails && (
              <TooltipProvider delayDuration={300}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button
                      type="button"
                      className="flex h-7 w-7 items-center justify-center rounded-[8px] text-[#7B6B72] transition-colors hover:bg-[#FAF7F1] hover:text-[#6E3450]"
                      onClick={(event) => {
                        event.stopPropagation();
                        if (onShowLineage) onShowLineage(metric);
                        else onClick?.();
                      }}
                      aria-label={onShowLineage ? `View data lineage for ${metric.label}` : `Open details for ${metric.label}`}
                    >
                      <Info className="h-3.5 w-3.5" />
                    </button>
                  </TooltipTrigger>
                  <TooltipContent
                    side="top"
                    align="start"
                    sideOffset={8}
                    className="max-w-sm rounded-[10px] border border-[#EDE6E1] bg-white px-4 py-3 text-sm text-[#1F1A20] shadow-[0_4px_6px_-1px_rgba(31,26,32,.06),0_12px_28px_-10px_rgba(110,52,80,.18)]"
                  >
                    <p className="leading-relaxed">
                      {metric.reasoning || 'Open calculation details, source data, and analysis flow.'}
                    </p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}
          </div>
        </header>

        <div className="flex min-w-0 flex-col gap-1.5">
          <div className="flex min-w-0 items-baseline gap-1.5 overflow-hidden">
            {valueParts.prefix && (
              <span className="font-display text-[22px] font-normal leading-none text-[#1F1A20]">
                {valueParts.prefix}
              </span>
            )}
            <span
              className={cn(
                "block min-w-0 max-w-full truncate font-display text-[clamp(1.75rem,2.8vw,2.125rem)] font-light leading-[1.02]",
                comingSoon && "text-[#8E858A]",
                !comingSoon && (presentation.tone === 'neutral' ? "text-[#1F1A20]" : tone.value)
              )}
              title={typeof metric.value === 'number'
                ? formatActual(metric.value, {
                    unit: metric.unit,
                    format: metricIsCurrencyPerUnit(metric) ? ',.4f' : undefined,
                  })
                : `${valueParts.prefix}${valueParts.primary}${valueParts.suffix}`}
            >
              {valueParts.primary}
            </span>
            {valueParts.suffix && (
              <span className="shrink-0 font-display text-[18px] font-normal text-[#7B6B72]">
                {valueParts.suffix}
              </span>
            )}
          </div>
        </div>

        {layout.showReferenceRows && referenceRows.length > 0 && (
          <div className="mb-1 mt-1">
            {referenceRows.map((comparator, comparatorIndex) => (
              <ReferenceRow
                key={`${comparator.basis || comparator.label || 'ref'}-${comparatorIndex}`}
                comparator={comparator}
                unit={metric.unit}
              />
            ))}
          </div>
        )}

        {comparisonComparators.length > 0 || referenceChips.length > 0 ? (
          <div className="flex flex-wrap items-center gap-1.5">
            {primaryChip && <DeltaChip comparator={primaryChip} primary unit={metric.unit} />}
            {secondaryChips.map((comparator, comparatorIndex) => (
              <DeltaChip
                key={`${comparator.basis || comparator.label || 'cmp'}-${comparatorIndex}`}
                comparator={comparator}
                primary={false}
                unit={metric.unit}
              />
            ))}
            {referenceChips.map((comparator, comparatorIndex) => (
              <ReferenceChip
                key={`${comparator.basis || comparator.label || 'ref-chip'}-${comparatorIndex}`}
                comparator={comparator}
              />
            ))}
            {metric.period && !metric.periodLabel && (
              <span className="ml-auto text-[10.5px] uppercase tracking-[0.08em] text-[#7B6B72]">
                {metric.period}
              </span>
            )}
            {comparisonComparators.length > visibleChips.length && (
              <span className="text-[10.5px] italic text-[#7B6B72]">
                +{comparisonComparators.length - visibleChips.length} more
              </span>
            )}
          </div>
        ) : showTrend && (
          <span className={cn("inline-flex w-fit max-w-full items-center rounded-full border px-2 py-0.5 text-[11px] font-medium", getTrendTone(metric))}>
            <span className="truncate">{metric.trendValue}</span>
          </span>
        )}

        {layout.showSpark && <MiniSparkline metric={metric} />}

        {comingSoon && (
          <span className="mt-auto w-fit rounded border border-[#D7D1D4] bg-[#EBE9EA] px-2 py-0.5 text-[10.5px] font-semibold uppercase tracking-[0.08em] text-[#8E858A]">
            Coming soon
          </span>
        )}

        {/* Source row removed — the column code chip + lineage panel
            already surface the provenance. The card-foot strip
            duplicated info and added visual noise. */}
      </div>
    </article>
  );
}
